// app/(tabs)/market/creer-produit.tsx
//
// Écran dédié à la création d'un produit (userpro). Atteint depuis
// gerer/[shopId].tsx → bouton "Créer un produit" (navigation push
// classique, pas de modale/pop-up).
//
// Reçoit shopId en paramètre d'URL (?shopId=...) pour savoir à quelle
// boutique rattacher le produit créé.
//
// FORMULAIRE COMPLET (patch V10) : reprend tous les champs désormais
// acceptés par create-product côté backend (voir hooks/market/
// useCreateProduct.ts → CreateProductFields). Organisé en 4 sections :
//   1. Essentiel      → nom, prix, catégorie (toujours visible)
//   2. Détails        → description, marque, état, stock, images
//      (toujours visible, deuxième priorité après l'essentiel)
//   3. Options avancées (accordéon, replié par défaut) → SKU, prix
//      promo, devise, mise en avant, boutons d'action (WhatsApp, Buy,
//      En savoir plus, Appeler), SEO, extra_data
//
// Design : cartes en accordéon avec transition de hauteur, disposition
// responsive (colonnes sur web large écran, empilé sur mobile via
// useWindowDimensions), palette alignée sur l'identité existante
// (#6610B1) mais hiérarchie typographique et espacement retravaillés
// pour une sensation plus soignée qu'un simple formulaire brut.
//
// PATCH V11 : le bouton de publication ("Créer") a été déplacé du
// footer fixe en bas d'écran vers le header, à droite du titre. Le
// footer a été supprimé — le scroll gagne donc en hauteur disponible.
// Le bouton reste piloté par les mêmes règles (isValid, creatingProduct)
// et déclenche toujours handleCreateProduct.
//
// Après création réussie, navigue vers l'écran d'édition dédié du
// produit : market/gerer-produit/[productId].tsx (router.replace, pas
// .push, pour ne pas laisser ce formulaire dans la pile de navigation).
//
// PATCH i18n : tous les textes affichés à l'utilisateur passent par
// react-i18next (namespace market.creerProduit / market.common). Les
// codes techniques (ex: 'new'/'used'/'refurbished' pour CONDITIONS,
// 'XOF' comme devise par défaut) restent inchangés — seuls les labels
// affichés sont traduits.

import React, { useState, useCallback, useMemo } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  KeyboardAvoidingView,
  ScrollView,
  Switch,
  useWindowDimensions,
  LayoutAnimation,
  UIManager,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';

import { useCreateProduct, type CreateProductFields, type ProductCondition } from '@/hooks/market/useCreateProduct';

const NATIVE = Platform.OS !== 'web';
const IS_ANDROID = Platform.OS === 'android';

if (IS_ANDROID && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

const BRAND = '#6610B1';
const BRAND_SOFT = '#F3E8FF';
const BRAND_TINT = '#F9F5FF';
const BORDER = '#E5D9FF';
const INK = '#222';
const MUTED = '#8A8496';

function haptic(style: Haptics.ImpactFeedbackStyle = Haptics.ImpactFeedbackStyle.Light) {
  if (NATIVE) Haptics.impactAsync(style);
}

function animateNext() {
  if (Platform.OS !== 'web') {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
  }
}

// ─── Champ texte réutilisable ────────────────────────────────────────────────

function FormField({
  label, value, onChangeText, placeholder, keyboardType, multiline, editable = true, hint,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  keyboardType?: 'default' | 'numeric' | 'email-address' | 'phone-pad' | 'url';
  multiline?: boolean;
  editable?: boolean;
  hint?: string;
}) {
  return (
    <View style={styles.fieldWrap}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput
        style={[styles.input, multiline && styles.inputMultiline]}
        placeholder={placeholder}
        placeholderTextColor="#B0A8C4"
        value={value}
        onChangeText={onChangeText}
        editable={editable}
        keyboardType={keyboardType === 'phone-pad' && Platform.OS === 'web' ? 'default' : keyboardType}
        multiline={multiline}
        numberOfLines={multiline ? 4 : 1}
        textAlignVertical={multiline ? 'top' : 'center'}
      />
      {!!hint && <Text style={styles.fieldHint}>{hint}</Text>}
    </View>
  );
}

// ─── Switch avec champ conditionnel (pattern show_X / X_url) ────────────────

function ToggleWithField({
  label, value, onValueChange, fieldLabel, fieldValue, onFieldChange, fieldPlaceholder, keyboardType,
}: {
  label: string;
  value: boolean;
  onValueChange: (v: boolean) => void;
  fieldLabel: string;
  fieldValue: string;
  onFieldChange: (v: string) => void;
  fieldPlaceholder?: string;
  keyboardType?: 'default' | 'phone-pad' | 'url';
}) {
  return (
    <View style={styles.toggleBlock}>
      <View style={styles.toggleRow}>
        <Text style={styles.toggleLabel}>{label}</Text>
        <Switch
          value={value}
          onValueChange={(v) => { animateNext(); onValueChange(v); }}
          trackColor={{ false: '#E5E1EC', true: BRAND }}
          thumbColor="#fff"
        />
      </View>
      {value && (
        <View style={styles.toggleFieldWrap}>
          <TextInput
            style={styles.input}
            placeholder={fieldPlaceholder}
            placeholderTextColor="#B0A8C4"
            value={fieldValue}
            onChangeText={onFieldChange}
            keyboardType={keyboardType === 'phone-pad' && Platform.OS === 'web' ? 'default' : keyboardType}
          />
          <Text style={styles.fieldHint}>{fieldLabel}</Text>
        </View>
      )}
    </View>
  );
}

// ─── Section repliable ────────────────────────────────────────────────────

function CollapsibleSection({
  title, subtitle, icon, expanded, onToggle, children,
}: {
  title: string;
  subtitle?: string;
  icon: keyof typeof Ionicons.glyphMap;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <View style={styles.card}>
      <TouchableOpacity
        style={styles.sectionHeader}
        activeOpacity={0.7}
        onPress={() => { animateNext(); haptic(); onToggle(); }}
      >
        <View style={styles.sectionHeaderIconWrap}>
          <Ionicons name={icon} size={18} color={BRAND} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.sectionHeaderTitle}>{title}</Text>
          {!!subtitle && <Text style={styles.sectionHeaderSubtitle}>{subtitle}</Text>}
        </View>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={20} color={MUTED} />
      </TouchableOpacity>
      {expanded && <View style={styles.sectionBody}>{children}</View>}
    </View>
  );
}

// ─── Écran principal ──────────────────────────────────────────────────────

export default function CreateProductScreen() {
  const router = useRouter();
  const { t } = useTranslation();
  const { shopId } = useLocalSearchParams<{ shopId: string }>();
  const { createProduct, loading: creatingProduct, error: createProductError } = useCreateProduct();
  const { width } = useWindowDimensions();

  const CONDITIONS: { value: ProductCondition; label: string }[] = [
    { value: 'new',         label: t('market.common.conditions.new') },
    { value: 'used',        label: t('market.common.conditions.used') },
    { value: 'refurbished', label: t('market.common.conditions.refurbished') },
  ];

  // Web large écran → mise en page en deux colonnes (formulaire | aperçu),
  // sinon empilé verticalement (mobile, ou web fenêtre étroite).
  const isWideLayout = Platform.OS === 'web' && width >= 900;

  // ── Section Essentiel ──
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [categoryId, setCategoryId] = useState('');

  // ── Section Détails ──
  const [descriptionShort, setDescriptionShort] = useState('');
  const [description, setDescription] = useState('');
  const [brand, setBrand] = useState('');
  const [condition, setCondition] = useState<ProductCondition>('new');
  const [stockQuantity, setStockQuantity] = useState('');

  // ── Options avancées (accordéon, replié par défaut) ──
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const [sku, setSku] = useState('');
  const [promoPrice, setPromoPrice] = useState('');
  const [currencyCode, setCurrencyCode] = useState('XOF');
  const [isFeatured, setIsFeatured] = useState(false);

  const [showWhatsapp, setShowWhatsapp] = useState(false);
  const [whatsappOverride, setWhatsappOverride] = useState('');
  const [showBuy, setShowBuy] = useState(false);
  const [buyUrl, setBuyUrl] = useState('');
  const [showLearnMore, setShowLearnMore] = useState(false);
  const [learnMoreUrl, setLearnMoreUrl] = useState('');
  const [showCall, setShowCall] = useState(false);
  const [callPhone, setCallPhone] = useState('');

  // ── SEO (accordéon, replié par défaut) ──
  const [seoExpanded, setSeoExpanded] = useState(false);
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');

  const handleBack = useCallback(() => {
    haptic();
    if (shopId) {
      router.replace(`/(tabs)/market/gerer/${shopId}`);
    } else {
      router.replace('/(tabs)/market/reglages');
    }
  }, [router, shopId]);

  const isValid = useMemo(() => {
    if (!name.trim() || !price.trim()) return false;
    const priceNum = Number(price);
    if (isNaN(priceNum) || priceNum < 0) return false;
    if (promoPrice.trim()) {
      const promoNum = Number(promoPrice);
      if (isNaN(promoNum) || promoNum < 0 || promoNum > priceNum) return false;
    }
    if (showBuy && !buyUrl.trim()) return false;
    if (showLearnMore && !learnMoreUrl.trim()) return false;
    if (showCall && !callPhone.trim()) return false;
    if (showWhatsapp && !whatsappOverride.trim()) return false;
    return true;
  }, [name, price, promoPrice, showBuy, buyUrl, showLearnMore, learnMoreUrl, showCall, callPhone, showWhatsapp, whatsappOverride]);

  const handleCreateProduct = useCallback(async () => {
    if (!shopId || !isValid || creatingProduct) return;

    haptic(Haptics.ImpactFeedbackStyle.Medium);

    const fields: CreateProductFields = {
      name: name.trim(),
      price: Number(price),
    };

    if (categoryId.trim()) fields.category_id = categoryId.trim();
    if (sku.trim()) fields.sku = sku.trim();
    if (descriptionShort.trim()) fields.description_short = descriptionShort.trim();
    if (description.trim()) fields.description = description.trim();
    if (brand.trim()) fields.brand = brand.trim();
    fields.condition = condition;
    if (stockQuantity.trim()) fields.stock_quantity = Number(stockQuantity);
    if (promoPrice.trim()) fields.promo_price = Number(promoPrice);
    if (currencyCode.trim()) fields.currency_code = currencyCode.trim().toUpperCase();
    fields.is_featured = isFeatured;

    fields.show_whatsapp_business_button = showWhatsapp;
    if (showWhatsapp) fields.whatsapp_business_phone_override = whatsappOverride.trim();

    fields.show_buy_button = showBuy;
    if (showBuy) fields.buy_url = buyUrl.trim();

    fields.show_learn_more_button = showLearnMore;
    if (showLearnMore) fields.learn_more_url = learnMoreUrl.trim();

    fields.show_call_button = showCall;
    if (showCall) fields.call_phone = callPhone.trim();

    if (seoTitle.trim()) fields.seo_title = seoTitle.trim();
    if (seoDescription.trim()) fields.seo_description = seoDescription.trim();

    const product = await createProduct(shopId, fields);
    if (product) {
      router.replace(`/(tabs)/market/gerer-produit/${product.id}`);
    }
  }, [
    shopId, isValid, creatingProduct, name, price, categoryId, sku, descriptionShort,
    description, brand, condition, stockQuantity, promoPrice, currencyCode, isFeatured,
    showWhatsapp, whatsappOverride, showBuy, buyUrl, showLearnMore, learnMoreUrl,
    showCall, callPhone, seoTitle, seoDescription, createProduct, router,
  ]);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      {/* ── Header avec bouton retour + bouton de publication ── */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backBtn} activeOpacity={0.7} onPress={handleBack}>
          <Ionicons name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('market.creerProduit.headerTitle')}</Text>
        <TouchableOpacity
          style={[
            styles.headerConfirmBtn,
            (!isValid || creatingProduct) && styles.headerConfirmBtnDisabled,
          ]}
          activeOpacity={0.8}
          onPress={handleCreateProduct}
          disabled={!isValid || creatingProduct}
        >
          {creatingProduct ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Text style={styles.headerConfirmBtnText}>{t('market.creerProduit.createButton')}</Text>
          )}
        </TouchableOpacity>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          isWideLayout && styles.scrollContentWide,
        ]}
        keyboardShouldPersistTaps="handled"
      >
        <View style={[styles.formColumn, isWideLayout && styles.formColumnWide]}>

          {/* ── Intro ── */}
          <View style={styles.iconBlock}>
            <View style={styles.iconWrap}>
              <Ionicons name="pricetag" size={30} color={BRAND} />
            </View>
            <Text style={styles.subtitle}>
              {t('market.creerProduit.intro')}
            </Text>
          </View>

          {/* ── 1. Essentiel ── */}
          <View style={styles.card}>
            <View style={styles.sectionHeaderStatic}>
              <View style={styles.sectionHeaderIconWrap}>
                <Ionicons name="star" size={18} color={BRAND} />
              </View>
              <Text style={styles.sectionHeaderTitle}>{t('market.creerProduit.sections.essential')}</Text>
            </View>
            <View style={styles.sectionBody}>
              <FormField
                label={t('market.creerProduit.fields.nameLabel')}
                value={name}
                onChangeText={setName}
                placeholder={t('market.creerProduit.fields.namePlaceholder')}
                editable={!creatingProduct}
              />
              <FormField
                label={t('market.creerProduit.fields.priceLabel', { currency: currencyCode || 'XOF' })}
                value={price}
                onChangeText={setPrice}
                placeholder={t('market.creerProduit.fields.pricePlaceholder')}
                keyboardType="numeric"
                editable={!creatingProduct}
              />
              <FormField
                label={t('market.creerProduit.fields.categoryLabel')}
                value={categoryId}
                onChangeText={setCategoryId}
                placeholder={t('market.creerProduit.fields.categoryPlaceholder')}
                editable={!creatingProduct}
                hint={t('market.creerProduit.fields.categoryHint')}
              />
            </View>
          </View>

          {/* ── 2. Détails ── */}
          <View style={styles.card}>
            <View style={styles.sectionHeaderStatic}>
              <View style={styles.sectionHeaderIconWrap}>
                <Ionicons name="document-text" size={18} color={BRAND} />
              </View>
              <Text style={styles.sectionHeaderTitle}>{t('market.creerProduit.sections.details')}</Text>
            </View>
            <View style={styles.sectionBody}>
              <FormField
                label={t('market.creerProduit.fields.descriptionShortLabel')}
                value={descriptionShort}
                onChangeText={setDescriptionShort}
                placeholder={t('market.creerProduit.fields.descriptionShortPlaceholder')}
                editable={!creatingProduct}
              />
              <FormField
                label={t('market.creerProduit.fields.descriptionLabel')}
                value={description}
                onChangeText={setDescription}
                placeholder={t('market.creerProduit.fields.descriptionPlaceholder')}
                multiline
                editable={!creatingProduct}
              />
              <FormField
                label={t('market.creerProduit.fields.brandLabel')}
                value={brand}
                onChangeText={setBrand}
                placeholder={t('market.creerProduit.fields.brandPlaceholder')}
                editable={!creatingProduct}
              />

              <Text style={styles.fieldLabel}>{t('market.creerProduit.fields.conditionLabel')}</Text>
              <View style={styles.chipRow}>
                {CONDITIONS.map((c) => (
                  <TouchableOpacity
                    key={c.value}
                    style={[styles.chip, condition === c.value && styles.chipActive]}
                    activeOpacity={0.7}
                    disabled={creatingProduct}
                    onPress={() => { haptic(); setCondition(c.value); }}
                  >
                    <Text style={[styles.chipText, condition === c.value && styles.chipTextActive]}>
                      {c.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              <FormField
                label={t('market.creerProduit.fields.stockLabel')}
                value={stockQuantity}
                onChangeText={setStockQuantity}
                placeholder={t('market.creerProduit.fields.stockPlaceholder')}
                keyboardType="numeric"
                editable={!creatingProduct}
                hint={t('market.creerProduit.fields.stockHint')}
              />
            </View>
          </View>

          {/* ── 3. Options avancées (accordéon) ── */}
          <CollapsibleSection
            title={t('market.creerProduit.sections.advanced')}
            subtitle={t('market.creerProduit.sections.advancedSubtitle')}
            icon="options"
            expanded={advancedExpanded}
            onToggle={() => setAdvancedExpanded((v) => !v)}
          >
            <FormField
              label={t('market.creerProduit.fields.skuLabel')}
              value={sku}
              onChangeText={setSku}
              placeholder={t('market.creerProduit.fields.skuPlaceholder')}
              editable={!creatingProduct}
              hint={t('market.creerProduit.fields.skuHint')}
            />
            <FormField
              label={t('market.creerProduit.fields.promoPriceLabel')}
              value={promoPrice}
              onChangeText={setPromoPrice}
              placeholder={t('market.creerProduit.fields.promoPricePlaceholder')}
              keyboardType="numeric"
              editable={!creatingProduct}
            />
            <FormField
              label={t('market.creerProduit.fields.currencyLabel')}
              value={currencyCode}
              onChangeText={(v) => setCurrencyCode(v.toUpperCase())}
              placeholder={t('market.creerProduit.fields.currencyPlaceholder')}
              editable={!creatingProduct}
              hint={t('market.creerProduit.fields.currencyHint')}
            />

            <View style={styles.toggleBlock}>
              <View style={styles.toggleRow}>
                <Text style={styles.toggleLabel}>{t('market.creerProduit.fields.featuredLabel')}</Text>
                <Switch
                  value={isFeatured}
                  onValueChange={setIsFeatured}
                  trackColor={{ false: '#E5E1EC', true: BRAND }}
                  thumbColor="#fff"
                  disabled={creatingProduct}
                />
              </View>
            </View>

            <View style={styles.divider} />
            <Text style={styles.groupLabel}>{t('market.creerProduit.actionButtons.groupLabel')}</Text>

            <ToggleWithField
              label={t('market.creerProduit.actionButtons.whatsapp')}
              value={showWhatsapp}
              onValueChange={setShowWhatsapp}
              fieldLabel={t('market.creerProduit.actionButtons.whatsappField')}
              fieldValue={whatsappOverride}
              onFieldChange={setWhatsappOverride}
              fieldPlaceholder={t('market.creerProduit.actionButtons.whatsappPlaceholder')}
              keyboardType="phone-pad"
            />
            <ToggleWithField
              label={t('market.creerProduit.actionButtons.buy')}
              value={showBuy}
              onValueChange={setShowBuy}
              fieldLabel={t('market.creerProduit.actionButtons.buyField')}
              fieldValue={buyUrl}
              onFieldChange={setBuyUrl}
              fieldPlaceholder="https://…"
              keyboardType="url"
            />
            <ToggleWithField
              label={t('market.creerProduit.actionButtons.learnMore')}
              value={showLearnMore}
              onValueChange={setShowLearnMore}
              fieldLabel={t('market.creerProduit.actionButtons.learnMoreField')}
              fieldValue={learnMoreUrl}
              onFieldChange={setLearnMoreUrl}
              fieldPlaceholder="https://…"
              keyboardType="url"
            />
            <ToggleWithField
              label={t('market.creerProduit.actionButtons.call')}
              value={showCall}
              onValueChange={setShowCall}
              fieldLabel={t('market.creerProduit.actionButtons.callField')}
              fieldValue={callPhone}
              onFieldChange={setCallPhone}
              fieldPlaceholder={t('market.creerProduit.actionButtons.whatsappPlaceholder')}
              keyboardType="phone-pad"
            />
          </CollapsibleSection>

          {/* ── 4. SEO (accordéon) ── */}
          <CollapsibleSection
            title={t('market.creerProduit.sections.seo')}
            subtitle={t('market.creerProduit.sections.seoSubtitle')}
            icon="search"
            expanded={seoExpanded}
            onToggle={() => setSeoExpanded((v) => !v)}
          >
            <FormField
              label={t('market.creerProduit.fields.seoTitleLabel')}
              value={seoTitle}
              onChangeText={setSeoTitle}
              placeholder={t('market.creerProduit.fields.seoTitlePlaceholder')}
              editable={!creatingProduct}
            />
            <FormField
              label={t('market.creerProduit.fields.seoDescriptionLabel')}
              value={seoDescription}
              onChangeText={setSeoDescription}
              placeholder={t('market.creerProduit.fields.seoDescriptionPlaceholder')}
              multiline
              editable={!creatingProduct}
            />
          </CollapsibleSection>

          {!!createProductError && (
            <View style={styles.errorBanner}>
              <Ionicons name="alert-circle-outline" size={16} color="#DC2626" />
              <Text style={styles.errorBannerText}>
                {createProductError === 'session_expired'
                  ? t('market.common.sessionExpiredReconnect2')
                  : createProductError === 'network_error'
                  ? t('market.common.networkError')
                  : createProductError}
              </Text>
            </View>
          )}

          <View style={{ height: 24 }} />
        </View>

        {/* ── Colonne aperçu (web large écran uniquement) ── */}
        {isWideLayout && (
          <View style={styles.previewColumn}>
            <View style={styles.previewCard}>
              <Text style={styles.previewLabel}>{t('market.creerProduit.preview.label')}</Text>
              <View style={styles.previewImagePlaceholder}>
                <Ionicons name="image-outline" size={32} color="#C4B5FD" />
              </View>
              <Text style={styles.previewName} numberOfLines={2}>
                {name.trim() || t('market.creerProduit.preview.namePlaceholder')}
              </Text>
              <Text style={styles.previewPrice}>
                {price.trim() ? `${Number(price).toLocaleString('fr-FR')} ${currencyCode || 'XOF'}` : `— ${currencyCode || 'XOF'}`}
              </Text>
              {!!promoPrice.trim() && !isNaN(Number(promoPrice)) && (
                <Text style={styles.previewPromo}>
                  {t('market.creerProduit.preview.promoLabel', {
                    price: `${Number(promoPrice).toLocaleString('fr-FR')} ${currencyCode || 'XOF'}`,
                  })}
                </Text>
              )}
              <View style={styles.previewBadgeRow}>
                <View style={styles.previewBadge}>
                  <Text style={styles.previewBadgeText}>
                    {CONDITIONS.find((c) => c.value === condition)?.label}
                  </Text>
                </View>
                {isFeatured && (
                  <View style={[styles.previewBadge, styles.previewBadgeFeatured]}>
                    <Ionicons name="star" size={11} color="#B45309" />
                    <Text style={[styles.previewBadgeText, styles.previewBadgeTextFeatured]}>{t('market.creerProduit.preview.featuredBadge')}</Text>
                  </View>
                )}
              </View>
              <Text style={styles.previewHint}>
                {t('market.creerProduit.preview.hint')}
              </Text>
            </View>
          </View>
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
    backgroundColor: '#fff',
  },
  backBtn: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '700',
    color: '#222',
    textAlign: 'center',
  },
  headerConfirmBtn: {
    backgroundColor: BRAND,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 8,
    minWidth: 64,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerConfirmBtnDisabled: {
    backgroundColor: '#C4B5FD',
  },
  headerConfirmBtnText: {
    fontSize: 13.5,
    fontWeight: '700',
    color: '#fff',
  },
  scrollView: {
    flex: 1,
    backgroundColor: '#FAFAFC',
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 24,
  },
  scrollContentWide: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 28,
    maxWidth: 1080,
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: 32,
  },
  formColumn: {
    width: '100%',
  },
  formColumnWide: {
    flex: 1,
    maxWidth: 620,
  },
  previewColumn: {
    width: 280,
    position: Platform.OS === 'web' ? ('sticky' as any) : undefined,
    top: Platform.OS === 'web' ? 24 : undefined,
  },
  previewCard: {
    backgroundColor: '#fff',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: BORDER,
    padding: 20,
    alignItems: 'center',
  },
  previewLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    alignSelf: 'flex-start',
    marginBottom: 14,
  },
  previewImagePlaceholder: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 14,
    backgroundColor: BRAND_TINT,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14,
  },
  previewName: {
    fontSize: 16,
    fontWeight: '800',
    color: INK,
    textAlign: 'center',
  },
  previewPrice: {
    fontSize: 18,
    fontWeight: '800',
    color: BRAND,
    marginTop: 6,
  },
  previewPromo: {
    fontSize: 12.5,
    color: '#16A34A',
    fontWeight: '600',
    marginTop: 2,
  },
  previewBadgeRow: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 12,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  previewBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#F3F4F6',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  previewBadgeFeatured: {
    backgroundColor: '#FEF3C7',
  },
  previewBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#4B5563',
  },
  previewBadgeTextFeatured: {
    color: '#B45309',
  },
  previewHint: {
    fontSize: 11.5,
    color: MUTED,
    textAlign: 'center',
    marginTop: 14,
    lineHeight: 16,
  },
  iconBlock: {
    alignItems: 'center',
    marginBottom: 24,
  },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: 18,
    backgroundColor: BRAND_SOFT,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14,
  },
  subtitle: {
    fontSize: 13.5,
    color: MUTED,
    textAlign: 'center',
    lineHeight: 19,
    paddingHorizontal: 8,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0EDF5',
    marginBottom: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 1,
  },
  sectionHeaderStatic: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#F5F2FA',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  sectionHeaderIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: BRAND_TINT,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sectionHeaderTitle: {
    fontSize: 14.5,
    fontWeight: '700',
    color: INK,
  },
  sectionHeaderSubtitle: {
    fontSize: 11.5,
    color: MUTED,
    marginTop: 1,
  },
  sectionBody: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 18,
  },
  fieldWrap: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 12.5,
    fontWeight: '600',
    color: '#666',
    marginBottom: 7,
  },
  fieldHint: {
    fontSize: 11,
    color: MUTED,
    marginTop: 5,
  },
  input: {
    backgroundColor: BRAND_TINT,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BORDER,
    paddingHorizontal: 14,
    paddingVertical: Platform.OS === 'ios' ? 14 : 10,
    fontSize: 15,
    color: '#333',
  },
  inputMultiline: {
    minHeight: 90,
    paddingTop: 12,
  },
  chipRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 999,
    backgroundColor: BRAND_TINT,
    borderWidth: 1,
    borderColor: BORDER,
  },
  chipActive: {
    backgroundColor: BRAND,
    borderColor: BRAND,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '600',
    color: BRAND,
  },
  chipTextActive: {
    color: '#fff',
  },
  toggleBlock: {
    marginBottom: 14,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  toggleLabel: {
    fontSize: 13.5,
    fontWeight: '600',
    color: INK,
    flex: 1,
    marginRight: 12,
  },
  toggleFieldWrap: {
    marginTop: 10,
  },
  divider: {
    height: 1,
    backgroundColor: '#F0EDF5',
    marginVertical: 12,
  },
  groupLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FEF2F2',
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginBottom: 12,
  },
  errorBannerText: {
    fontSize: 12.5,
    color: '#DC2626',
    fontWeight: '600',
    flex: 1,
  },
});
